/**
 * Stands between the Connect button and the CRM's sign-in.
 *
 * Connecting a CRM lets Waterlily read contacts from it and write a note back into it, and the
 * advisor has to agree to that before the connection is made — not after. `requestConnect` goes
 * straight on when the agreement is already recorded; otherwise it opens a dialog, and `agree`
 * records the consent and only then continues to `onAgreed`, which does the actual connecting.
 *
 * The wording shown is what gets stored with the consent, including the sentence about who can see
 * the note, which differs per CRM. The integrations service checks the same consent again on the
 * connect calls, so this gate is the way to give the agreement, not the thing enforcing it.
 */
import { useState } from 'react';
import { useCrmConsent } from '@client/hooks/useCrmConsent';

export const CONNECTION_ACKNOWLEDGEMENT =
  'I understand what Waterlily will read from my CRM, and that it will write a summary note back when I link a contact.';

export function connectionConsentText(crmName: string, noteDisclosure: string): string {
  return (
    `Connecting ${crmName} lets Waterlily read the contacts you choose to work with, and write a ` +
    `summary note back to a contact's record when you link them to a Waterlily client. You can ` +
    `disconnect at any time. ${noteDisclosure}`
  ).trim();
}

export function useCrmConnectionConsentGate({
  advisorId,
  provider,
  crmName,
  onAgreed,
}: {
  advisorId: string;
  provider: string;
  crmName: string;
  onAgreed: () => void;
}) {
  const consent = useCrmConsent({ advisorId, provider });
  const [isOpen, setIsOpen] = useState(false);
  const [isAcknowledged, setIsAcknowledged] = useState(false);

  const text = connectionConsentText(crmName, consent.noteDisclosure);

  const requestConnect = () => {
    if (consent.hasConnectionConsent) {
      onAgreed();
      return;
    }
    // A failed save from an earlier attempt should not greet the advisor when they try again.
    consent.clearError();
    setIsAcknowledged(false);
    setIsOpen(true);
  };

  const agree = async () => {
    try {
      await consent.give('connection', `${text} ${CONNECTION_ACKNOWLEDGEMENT}`);
    } catch {
      // The dialog stays open and shows `error`; nothing is connected without a recorded consent.
      return;
    }
    setIsOpen(false);
    onAgreed();
  };

  return {
    isChecking: consent.isLoading,
    isOpen,
    text,
    acknowledgement: CONNECTION_ACKNOWLEDGEMENT,
    isAcknowledged,
    setIsAcknowledged,
    requestConnect,
    agree,
    cancel: () => setIsOpen(false),
    isSaving: consent.isSaving,
    error: consent.error,
  };
}
